import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import Packages from "@/components/Packages";
import LeadForm from "@/components/LeadForm";
import SampleAdsCTASection from "@/components/SampleAdsCTASection";
import { MeetTheTeamLink } from "@/components/MeetTheTeam";
import {
  ArrowRight,
} from "lucide-react";

const Home = () => {
  return (
    <SiteShell>
      {/* HERO */}
      <section
        className="relative bg-[var(--af-navy)] text-[var(--af-cream)] grid-bg overflow-hidden"
        data-testid="home-hero"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-24 sm:pt-20 sm:pb-32 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <div className="eyebrow mb-6">
                Anne Flinn Advertising · Charlottesville, VA
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-7 leading-[1.05]">
                Advertise{" "}
                <span className="text-[var(--af-lime)]">smarter.</span>{" "}
                Win your next{" "}
                <span className="text-[var(--af-lime)]">90 days.</span>
              </h1>
              <p className="text-lg sm:text-xl text-[var(--af-cream)]/75 max-w-2xl mb-8 leading-relaxed">
                A boutique advertising agency built for two worlds, home
                services pros chasing busy season, and tourism brands chasing
                qualified visitors. Multi-state reach, woman-owned, locally
                run.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/home-services"
                  className="btn-lime"
                  data-testid="home-cta-home-services"
                >
                  Home Services <ArrowRight size={14} />
                </Link>
                <Link
                  to="/tourism"
                  className="btn-ghost"
                  data-testid="home-cta-tourism"
                >
                  Tourism <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div
                className="surface p-7 fade-up"
                style={{ animationDelay: ".15s" }}
              >
                <div className="eyebrow mb-3">We know what&apos;s riding on this</div>
                <div className="font-display text-4xl leading-none mb-3">
                  Your budgets.
                </div>
                <div className="font-display text-4xl leading-none mb-3 text-[var(--af-lime)]">
                  Your season.
                </div>
                <div className="font-display text-4xl leading-none">
                  Our expertise.
                </div>
                <p className="text-sm mt-6 text-[var(--af-cream)]/70 leading-relaxed">
                  Advertising isn&apos;t a side bet, it&apos;s the engine that fills
                  calendars and pays crews. &ldquo;Let&apos;s test it and see&rdquo; isn&apos;t a
                  strategy you can afford. So we don&apos;t make you guess. We have
                  hand-crafted these easy-to-launch starter packages for you
                  to run today, based entirely on our knowledge and
                  previous successful launches in your industry. Have
                  confidence in your advertising buy so you can get back to
                  what you love most: running the business.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DASHED DIVIDER */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="dashed-divider" />
      </div>

      {/* PACKAGES */}
      <Packages vertical="general" />

      <SampleAdsCTASection
        label="See Our Work"
        audience="general"
        testIdPrefix="home-sample-ads"
      />

      {/* LEAD FORM */}
      <section
        id="lead-form"
        className="grid-bg px-5 sm:px-8 py-24 border-t border-[var(--af-line)]"
        data-testid="home-lead-form-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="eyebrow mb-4">Take the First Step</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6">
              20 minutes.
              <br />
              A real plan.
              <br />
              <span className="text-[var(--af-lime)]">No sales theater.</span>
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-md mb-7 leading-relaxed">
              Tell us your category and your goal. We&apos;ll come back with which
              markets to target, what offer to lead with, and what your 90-day
              numbers should look like.
            </p>
            <ul className="space-y-2.5 text-sm text-[var(--af-cream)]/75">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Free 20-minute market audit
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Custom playbook for your category
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                No obligation, no spam
              </li>
            </ul>
            <div className="mt-6">
              <MeetTheTeamLink />
            </div>
          </div>
          <LeadForm
            trade="general"
            tradeName="Anne Flinn Advertising"
            headline="Tell us about your business."
          />
        </div>
      </section>
    </SiteShell>
  );
};

export default Home;
